import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle2, User, Briefcase, GraduationCap, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput, normalizePhoneNumber } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CVUploader } from '../components';
import { documentsService, candidatesService } from '../services';
import type { ExtractedCVData, SeniorityLevel, AvailabilityStatus } from '../types';

const seniorityOptions = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Confirmé' },
  { value: 'senior', label: 'Senior' },
  { value: 'expert', label: 'Expert / Lead' },
];

const availabilityOptions = [
  { value: 'available', label: 'Disponible immédiatement' },
  { value: 'open', label: 'Ouvert aux opportunités' },
  { value: 'not_looking', label: 'Non disponible' },
];

function parseYearsExperience(nbExp: string): number | undefined {
  const match = nbExp.match(/\d+/);
  return match ? parseInt(match[0]) : undefined;
}

function estimateSeniority(years?: number): SeniorityLevel | undefined {
  if (!years) return undefined;
  if (years < 3) return 'junior';
  if (years < 6) return 'mid';
  if (years < 10) return 'senior';
  return 'expert';
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currentTitle: string;
  locationCity: string;
  yearsExperience?: number;
  seniority?: SeniorityLevel;
  availability?: AvailabilityStatus;
}

export function CVImportPage() {
  const [extractedData, setExtractedData] = useState<ExtractedCVData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentTitle: '',
    locationCity: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [tempCandidateId, setTempCandidateId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applyWarning, setApplyWarning] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [existingCandidateId, setExistingCandidateId] = useState<number | null>(null);
  const [duplicateEmail, setDuplicateEmail] = useState<string>('');
  const [wasUpdated, setWasUpdated] = useState(false);
  const tempCandidateIdRef = useRef<number | null>(null);
  const createdIdRef = useRef<number | null>(null);

  // Garder les refs synchronisées pour le cleanup
  useEffect(() => {
    tempCandidateIdRef.current = tempCandidateId;
  }, [tempCandidateId]);

  useEffect(() => {
    createdIdRef.current = createdId;
  }, [createdId]);

  // Cleanup du candidat temporaire quand on quitte la page sans finaliser
  useEffect(() => {
    return () => {
      const idToClean = tempCandidateIdRef.current;
      const wasCreated = createdIdRef.current;
      if (idToClean && !wasCreated) {
        // Supprimer le candidat temporaire en arrière-plan
        candidatesService.delete(idToClean).catch(() => {
          // Ignorer les erreurs de suppression
        });
      }
    };
  }, []);

  const handleExtracted = useCallback((data: ExtractedCVData) => {
    setExtractedData(data);
    setError(null);

    const years = parseYearsExperience(data.nbExp);
    const rawEmail = data.coordonnees_personnelles.email || '';
    // Valider le format email extrait (l'OCR peut mal lire le @)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = emailRegex.test(rawEmail) ? rawEmail : '';

    setFormData({
      firstName: data.coordonnees_personnelles.prenom || '',
      lastName: data.coordonnees_personnelles.nom || '',
      email,
      phone: normalizePhoneNumber(data.coordonnees_personnelles.telephone || ''),
      currentTitle: data.titre_poste || '',
      locationCity: data.coordonnees_personnelles.ville || '',
      yearsExperience: years,
      seniority: estimateSeniority(years),
      availability: 'available',
    });
  }, []);

  const handleUploadWithExtraction = useCallback(
    async (file: File, onProgress?: (progress: number) => void) => {
      setUploadedFile(file);
      let candidateId = tempCandidateId;

      if (!candidateId) {
        const tempCandidate = await candidatesService.create({
          firstName: 'Import',
          lastName: 'CV',
          source: 'cv_import',
        });
        candidateId = tempCandidate.id;
        setTempCandidateId(candidateId);
      }

      return documentsService.uploadAndExtract(candidateId, file, onProgress);
    },
    [tempCandidateId]
  );

  const handleInputChange = (field: keyof FormData, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateCandidate = async () => {
    if (!extractedData) return;

    setIsCreating(true);
    setError(null);

    try {
      // Si le candidat temporaire n'existe plus (supprimé entre-temps), en recréer un
      let candidateId = tempCandidateId;
      const candidateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        currentTitle: formData.currentTitle || undefined,
        locationCity: formData.locationCity || undefined,
        yearsExperience: formData.yearsExperience,
        seniority: formData.seniority,
        availability: formData.availability,
        source: 'cv_import' as const,
      };

      if (candidateId) {
        try {
          await candidatesService.update(candidateId, candidateData);
        } catch (updateErr: any) {
          if (updateErr?.response?.status === 404) {
            // Candidat supprimé entre-temps, en créer un nouveau
            const newCandidate = await candidatesService.create(candidateData);
            candidateId = newCandidate.id;
            setTempCandidateId(candidateId);
            // Ré-uploader le CV vers le nouveau candidat
            if (uploadedFile) {
              await documentsService.uploadAndExtract(candidateId, uploadedFile);
            }
          } else {
            throw updateErr;
          }
        }
      } else {
        const newCandidate = await candidatesService.create(candidateData);
        candidateId = newCandidate.id;
        setTempCandidateId(candidateId);
      }

      try {
        const applyResult = await documentsService.applyExtractedData(candidateId, extractedData, {
          updateBasicInfo: false,
          replaceExperiences: true,
          replaceEducations: true,
          replaceSkills: true,
          overwriteExisting: false,
        });
        if (applyResult.errors && applyResult.errors.length > 0) {
          setApplyWarning(
            `Certaines données n'ont pas pu être enregistrées : ${applyResult.errors.join('. ')}`
          );
        }
      } catch (applyError: any) {
        const msg = applyError?.response?.data?.message || applyError?.message || 'Erreur inconnue';
        setApplyWarning(
          `L'application des données extraites a échoué : ${msg}. ` +
          `Les compétences, formations et expériences n'ont pas pu être enregistrées.`
        );
      }

      // Le candidat est désormais finalisé : ce n'est plus un brouillon temporaire.
      // On efface tempCandidateId pour qu'un reset (« Importer un autre CV ») ou le
      // cleanup au démontage ne le supprime PAS par erreur.
      setTempCandidateId(null);
      setWasUpdated(false);
      setCreatedId(candidateId);
    } catch (err: any) {
      const responseData = err?.response?.data;
      const status = err?.response?.status;

      // Détecter le conflit email avec un candidat existant
      if (status === 409 && responseData?.error?.details?.existingCandidateId) {
        setExistingCandidateId(responseData.error.details.existingCandidateId);
        setDuplicateEmail(formData.email);
        setShowDuplicateDialog(true);
        setIsCreating(false);
        return;
      }

      const validationErrors = responseData?.details?.errors;
      if (validationErrors && Array.isArray(validationErrors)) {
        setError(`Erreur de validation : ${validationErrors.join(', ')}`);
      } else {
        setError(responseData?.error?.message || responseData?.message || 'Une erreur est survenue lors de la création');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpsertExisting = async () => {
    if (!extractedData || !existingCandidateId) return;

    setShowDuplicateDialog(false);
    setIsCreating(true);
    setError(null);

    try {
      const candidateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        currentTitle: formData.currentTitle || undefined,
        locationCity: formData.locationCity || undefined,
        yearsExperience: formData.yearsExperience,
        seniority: formData.seniority,
        availability: formData.availability,
      };

      // 1. Mettre à jour le candidat existant
      await candidatesService.update(existingCandidateId, candidateData);

      // 2. Uploader le CV vers le candidat existant
      if (uploadedFile) {
        await documentsService.uploadCV(existingCandidateId, uploadedFile);
      }

      // 3. Appliquer les données extraites du CV
      try {
        const applyResult = await documentsService.applyExtractedData(
          existingCandidateId,
          extractedData,
          {
            updateBasicInfo: false,
            replaceExperiences: true,
            replaceEducations: true,
            replaceSkills: true,
            overwriteExisting: true,
          },
        );
        if (applyResult.errors && applyResult.errors.length > 0) {
          setApplyWarning(
            `Certaines données n'ont pas pu être enregistrées : ${applyResult.errors.join('. ')}`,
          );
        }
      } catch (applyError: any) {
        const msg = applyError?.response?.data?.message || applyError?.message || 'Erreur inconnue';
        setApplyWarning(
          `L'application des données extraites a échoué : ${msg}. ` +
          `Les compétences, formations et expériences n'ont pas pu être enregistrées.`,
        );
      }

      // 4. Supprimer le candidat temporaire
      if (tempCandidateId) {
        try {
          await candidatesService.delete(tempCandidateId);
        } catch {
          // Ignorer les erreurs de suppression
        }
        setTempCandidateId(null);
      }

      setWasUpdated(true);
      setCreatedId(existingCandidateId);
    } catch (err: any) {
      const responseData = err?.response?.data;
      setError(responseData?.error?.message || responseData?.message || 'Une erreur est survenue lors de la mise à jour');
    } finally {
      setIsCreating(false);
    }
  };

  const handleReset = async () => {
    // Supprimer le candidat temporaire si existant
    if (tempCandidateId) {
      try {
        await candidatesService.delete(tempCandidateId);
      } catch {
        // Ignorer les erreurs de suppression
      }
    }

    setExtractedData(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      currentTitle: '',
      locationCity: '',
    });
    setTempCandidateId(null);
    setUploadedFile(null);
    setError(null);
    setApplyWarning(null);
    setResetKey((prev) => prev + 1); // Force CVUploader to remount
  };

  if (createdId) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="border-success bg-success-light">
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center">
              <CheckCircle2 className="h-16 w-16 text-success mb-4" />
              <h2 className="text-2xl font-semibold text-ink mb-2">
                {wasUpdated ? 'Candidat mis à jour avec succès' : 'Candidat créé avec succès'}
              </h2>
              <p className="text-[var(--color-text-secondary)] mb-4">
                {applyWarning
                  ? `Le candidat a été ${wasUpdated ? 'mis à jour' : 'créé'} mais certaines données n'ont pas pu être appliquées.`
                  : 'Les données du CV ont été extraites et appliquées au profil.'}
              </p>
              {applyWarning && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 max-w-lg">
                  {applyWarning}
                </div>
              )}
              <div className="flex gap-3">
                <Link to={`/itc/candidates/${createdId}`}>
                  <Button className="bg-bpa-accent hover:bg-bpa-accent-hover">
                    Voir le profil
                  </Button>
                </Link>
                <Button variant="outline" onClick={() => {
                  setCreatedId(null);
                  handleReset();
                }}>
                  Importer un autre CV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <Link to="/itc/candidates">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
        </Link>
        {extractedData && (
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Recommencer
          </Button>
        )}
      </div>

      <div>
        <p className="overline mb-2">ITC</p>
        <h1 className="heading-1">Importer un CV</h1>
        <p className="text-[var(--color-text-secondary)] mt-2">
          Téléchargez un CV (PDF, DOCX ou image) pour créer automatiquement un nouveau candidat.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-error-light border border-error rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <CVUploader
            key={resetKey}
            onExtracted={handleExtracted}
            uploadFn={handleUploadWithExtraction}
            disabled={!!extractedData}
          />
        </div>

        <Card className={`border-[var(--color-border)] transition-opacity ${!extractedData ? 'opacity-50' : ''}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-bpa-accent" />
              Informations extraites
            </CardTitle>
            <CardDescription>
              Vérifiez et complétez les informations avant de créer le candidat.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(val) => handleInputChange('phone', val)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="currentTitle">Poste actuel</Label>
                <Input
                  id="currentTitle"
                  value={formData.currentTitle}
                  onChange={(e) => handleInputChange('currentTitle', e.target.value)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="locationCity">Ville</Label>
                <Input
                  id="locationCity"
                  value={formData.locationCity}
                  onChange={(e) => handleInputChange('locationCity', e.target.value)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="yearsExperience">Années d'expérience</Label>
                <Input
                  id="yearsExperience"
                  type="number"
                  value={formData.yearsExperience || ''}
                  onChange={(e) => handleInputChange('yearsExperience', e.target.value ? parseInt(e.target.value) : undefined)}
                  disabled={!extractedData}
                />
              </div>
              <div className="space-y-2">
                <Label>Séniorité</Label>
                <Select
                  value={formData.seniority || ''}
                  onValueChange={(value) => handleInputChange('seniority', value as SeniorityLevel)}
                  disabled={!extractedData}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {seniorityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Disponibilité</Label>
                <Select
                  value={formData.availability || ''}
                  onValueChange={(value) => handleInputChange('availability', value as AvailabilityStatus)}
                  disabled={!extractedData}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availabilityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {extractedData && (
              <div className="mt-6 space-y-4">
                {extractedData.experiences_professionnelles.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="h-4 w-4 text-copper" />
                      <span className="text-sm font-medium text-ink">
                        {extractedData.experiences_professionnelles.length} expérience(s)
                      </span>
                    </div>
                    <div className="text-xs text-[var(--color-text-secondary)] space-y-1">
                      {extractedData.experiences_professionnelles.slice(0, 3).map((exp, idx) => (
                        <p key={idx}>
                          {exp.titre_poste} chez {exp.entreprise}
                        </p>
                      ))}
                      {extractedData.experiences_professionnelles.length > 3 && (
                        <p className="text-[var(--color-text-tertiary)]">
                          +{extractedData.experiences_professionnelles.length - 3} autres
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {extractedData.diplomes_certifications.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="h-4 w-4 text-copper" />
                      <span className="text-sm font-medium text-ink">
                        {extractedData.diplomes_certifications.length} formation(s)
                      </span>
                    </div>
                    <div className="text-xs text-[var(--color-text-secondary)] space-y-1">
                      {extractedData.diplomes_certifications.slice(0, 2).map((edu, idx) => (
                        <p key={idx}>
                          {edu.nom} - {edu.etablissement}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="pt-4">
              <Button
                className="w-full bg-bpa-accent hover:bg-bpa-accent-hover"
                disabled={!extractedData || isCreating || !formData.firstName || !formData.lastName}
                onClick={handleCreateCandidate}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Créer le candidat
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog : candidat avec email déjà existant */}
      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Candidat existant</AlertDialogTitle>
            <AlertDialogDescription>
              Un candidat avec l&apos;email <strong>{duplicateEmail}</strong> existe
              déjà dans votre base. Souhaitez-vous mettre à jour son profil avec
              les données du CV importé ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpsertExisting}
              className="bg-bpa-accent hover:bg-bpa-accent-hover"
            >
              Mettre à jour le candidat existant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
